/**
 *
 * @module dustClientPayload
 *
 */

import Check from 'check-types';
import uuid from 'uuid';
import DustDevicePayload from './dustDevicePayload';
import DustApplicationPayload from './dustApplicationPayload';
import DustSdkPayload from './dustSdkPayload';
import Util from './../../../services/util/entry';

import InvalidArgumentException from './../../exception/invalidArgumentException';

const {
    instanceStrict,
    nonEmptyString,
    string,
    object,
    date,
    nonEmptyObject
} = Check.assert;

/**
 *
 * @class DustClientPayload
 * @since 3.4.0
 * @desc describes the client payload which contains all non-server related data
 *
 */
export default class DustClientPayload {

    /**
     *
     * @access private
     * @param {Object} options
     * @param {String} options.category
     * @param {SDK.Services.Internal.Dust.DustDevicePayload} options.device
     * @param {Object} [options.data={}]
     * @param {String} options.event
     * @param {SDK.Services.Internal.Dust.DustApplicationPayload} options.application
     * @param {SDK.Services.Internal.Dust.DustSdkPayload} options.sdk
     * @param {String} [options.transactionId]
     * @param {Object} [options.correlationIds]
     * @param {String} [options.timestamp=new Date().toISOString()]
     * @throws {InvalidArgumentException}
     *
     */
    constructor(options) {

        const errorMessage = {
            ctor: 'DustClientPayload(options)',
            get options() {
                return `${this.ctor} options should be a non-empty Object`;
            },
            get category() {
                return `${this.ctor} options.category should be a String with length`;
            },
            get device() {
                return `${this.ctor} options.device should be an instance of SDK.Services.Internal.Dust.DustDevicePayload`; // eslint-disable-line max-len
            },
            get data() {
                return `${this.ctor} options.data should be an Object`;
            },
            get event() {
                return `${this.ctor} options.event should be a String with length`;
            },
            get application() {
                return `${this.ctor} options.application should be an instance of SDK.Services.Internal.Dust.DustApplicationPayload`; // eslint-disable-line max-len
            },
            get sdk() {
                return `${this.ctor} options.sdk should be an instance of SDK.Services.Internal.Dust.DustSdkPayload`;
            },
            get transactionId() {
                return `${this.ctor} options.transactionId should be a String`;
            },
            get correlationIds() {
                return `${this.ctor} options.correlationIds should be an Object`;
            },
            get timestamp() {
                return `${this.ctor} options.timestamp should be an ISO Date String`;
            }
        };

        nonEmptyObject(options, errorMessage.options, InvalidArgumentException);

        const { category, device, data = {}, event, application, sdk, transactionId, correlationIds } = options;
        const { timestamp = new Date().toISOString() } = options;

        nonEmptyString(category, errorMessage.category, InvalidArgumentException);
        instanceStrict(device, DustDevicePayload, errorMessage.device, InvalidArgumentException);
        object(data, errorMessage.data, InvalidArgumentException);
        nonEmptyString(event, errorMessage.event, InvalidArgumentException);
        instanceStrict(application, DustApplicationPayload, errorMessage.application, InvalidArgumentException);
        instanceStrict(sdk, DustSdkPayload, errorMessage.sdk, InvalidArgumentException);

        if (Check.assigned(transactionId)) {
            string(transactionId, errorMessage.transactionId, InvalidArgumentException);
        }

        if (Check.assigned(correlationIds)) {
            object(correlationIds, errorMessage.correlationIds, InvalidArgumentException);
        }

        if (!Util.dateRegex(timestamp)) {
            date(null, errorMessage.timestamp, InvalidArgumentException);
        }

        /**
         *
         * @access private
         * @type {String}
         *
         */
        this.category = category;

        /**
         *
         * @access private
         * @type {SDK.Services.Internal.Dust.DustDevicePayload}
         *
         */
        this.device = device;

        /**
         *
         * @access private
         * @type {Object}
         *
         */
        this.data = data;

        /**
         *
         * @access private
         * @type {String}
         *
         */
        this.event = event;

        /**
         *
         * @access private
         * @type {String}
         * @desc RFC4122 UUID used to identify this payload
         * @note typecheck is not needed for this because it's generated directly and not passed into the constructor
         *
         */
        this.eventId = uuid.v4();

        /**
         *
         * @access private
         * @type {SDK.Services.Internal.Dust.DustApplicationPayload}
         *
         */
        this.application = application;

        /**
         *
         * @access private
         * @type {SDK.Services.Internal.Dust.DustSdkPayload}
         *
         */
        this.sdk = sdk;

        /**
         *
         * @access private
         * @type {String|undefined}
         * @note the JS SDK does not currently support this property
         *
         */
        this.transactionId = transactionId;

        /**
         *
         * @access private
         * @type {Object|undefined}
         * @note the JS SDK does not currently support this property
         *
         */
        this.correlationIds = correlationIds;

        /**
         *
         * @access private
         * @type {String}
         * @note ISO 8601/RFC-3339 timestamp
         *
         */
        this.timestamp = timestamp;

    }

}



// WEBPACK FOOTER //
// ./src/services/internal/dust/dustClientPayload.js